import React from "react";
import Modal from "../../../../_components/Modal";
import Button from "../../../../_components/Button";
import Text from "../../../../_components/Text";
import Toast from "../../../../_components/Toast";

import {
  formatCurrencyValue,
  decimalFormat,
  supportPhoneNumber,
} from "../../../../utils";
import { getPlans as get, upgradeBranchPlan } from "../../../../api/plans";
import { CardHorizontal, PlanWrapper, Icon } from "./components";
import { useShowMe } from "./hooks";

import AppContext from "../../context";
import { useLocation } from "react-router-dom";
import EasyButton from "../../../../_components/Button/EasyButton";

function toCents(value) {
  if (value === null || value === undefined) return 0;
  const s0 = String(value).trim().replace(/\s/g, "").replace(",", ".");
  const neg = s0.startsWith("-");
  const s = neg ? s0.slice(1) : s0;

  const [i, f = ""] = s.split(".");
  const intPart = parseInt(i || "0", 10);
  const fracPart = parseInt((f + "00").slice(0, 2), 10);

  const cents = intPart * 100 + (isNaN(fracPart) ? 0 : fracPart);
  return neg ? -cents : cents;
}

function roundHalfEven(numerator, denominator) {
  const q = Math.trunc(numerator / denominator);
  const r = numerator % denominator;

  const absR = Math.abs(r);
  const absD = Math.abs(denominator);
  const sign = numerator * denominator >= 0 ? 1 : -1;

  const twiceR = absR * 2;

  if (twiceR < absD) return q;
  if (twiceR > absD) return q + sign;

  return q % 2 === 0 ? q : q + sign;
}

function calcDiscountCents(priceCents, period, payableWith) {
  if (payableWith !== "credit_card") return 0;

  if (period === "month") {
    return roundHalfEven(priceCents * 5, 100);
  }

  if (period === "year") {
    return roundHalfEven(priceCents * 2, 12);
  }

  return 0;
}

function PayMethodPills({ active, onChange, disabled, isAnnual }) {
  return (
    <div
      className="d-flex flex-column align-items-center"
      style={{ marginTop: 10 }}
      onClick={(e) => e.stopPropagation()}
    >
      <small style={{ color: "white", opacity: 0.9 }}>Forma de pagamento</small>

      <div
        className="btn-group"
        role="group"
        aria-label="Forma de pagamento"
        style={{ marginTop: 6 }}
      >
        <button
          type="button"
          className={`btn btn-sm ${
            active === "credit_card" ? "btn-primary" : "btn-outline-primary"
          }`}
          disabled={disabled}
          onClick={() => onChange("credit_card")}
          title={isAnnual ? "Pagamento no cartão de crédito" : "Cartão: 5% de desconto"}
        >
          <i className="fas fa-credit-card" />{" "}
          {isAnnual ? "Cartão" : "Cartão • -5%"}
        </button>

        <button
          type="button"
          className={`btn btn-sm ${
            active === "pix" ? "btn-primary" : "btn-outline-primary"
          }`}
          disabled={disabled}
          onClick={() => onChange("pix")}
          title="PIX: sem desconto"
        >
          PIX
        </button>
      </div>
    </div>
  );
}

export default function UpgradeDePlano(props) {
  const {
    open = false,
    onRequestClose = () => {},
    onSwitchToAvulso = () => {},
    active,
    hideButton = false,
  } = props;

  const context = React.useContext(AppContext);
  const ref = React.useRef(null);
  const handleShowMe = useShowMe();
  const location = useLocation();

  const [toast, setToast] = React.useState({ show: false, message: "", type: "" });
  const [plans, setPlans] = React.useState([]);
  const [fetchingPlans, setFetchingPlans] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [show, setShow] = React.useState(false);
  const [selectedPlan, setPlan] = React.useState({});
  const [title, setTitle] = React.useState("");
  const [openedByParent, setOpenedByParent] = React.useState(false);

  const [planType, setPlanType] = React.useState("mensal");
  const [payableWithByPlanId, setPayableWithByPlanId] = React.useState({});
  
  const submitLock = React.useRef(false);

  function onChangePayableWith(planId, value) {
    setPayableWithByPlanId((prev) => ({ ...prev, [planId]: value }));
  }

  React.useEffect(() => {
    setShow(handleShowMe(context, location.pathname));
  }, [context.currentBranch, location.pathname]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  React.useEffect(() => {
    if (open && !showModal && !fetchingPlans) {
      setOpenedByParent(true);
      getPlans();
    }
    if (!open && showModal && openedByParent) {
      setShowModal(false);
      setPlan({});
      setOpenedByParent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function getPlans() {
    setFetchingPlans(true);
    get()
      .then((data) => {
        const notesQuantity = context.currentBranch.branch_plan.notes_quantity;
        setTitle(`Assinatura atual: ${context.currentBranch.branch_plan.plan_name}`);

        const nextPlans = data
          .filter((plan) => plan.notes_quantity > notesQuantity)
          .map((plan) => ({ ...plan, price: parseFloat(plan.price) }));

        setPlans(nextPlans);
        setShowModal(true);
        setTimeout(() => setFetchingPlans(false), 300);
      })
      .catch(() => {
        setToast({ show: true, type: "error", message: "Não foi possível carregar os planos." });
        setTimeout(() => setFetchingPlans(false), 300);
      });
  }

  const filteredPlans = plans.filter((p) => {
    const isAnnual = (p.iugu_id || "").startsWith("anual");
    return planType === "anual" ? isAnnual : !isAnnual;
  });

  async function canUpgrade(plan) {
    if (submitLock.current) return;
    submitLock.current = true;
    
    setToast({ show: false, message: "", type: "" });

    const isAnnual = (plan.iugu_id || "").startsWith("anual");
    const period = isAnnual ? "year" : "month";

    const payable_with = payableWithByPlanId[plan.id] || "credit_card";

    setUpdating(true);

    try {
      const resp = await upgradeBranchPlan({ plan: plan.id, payable_with, period });
      window.open(resp.url, "_blank");
      window.location.href = "/plan/branch-invoices";
    } catch (error) {
      submitLock.current = false;
      setUpdating(false);
      setShowModal(false);
      setPlan({});
      if (error.pending_invoices) {
        setToast({ show: true, message: "Você possui faturas pendentes!", type: "error" });
      } else {
        setToast({ show: true, message: "Não foi possível iniciar o upgrade.", type: "error" });
      }
    }
  }

  function renderPlanPrice(plan) {
    const isNegotiation = plan.id === "7945dc1a-1722-43db-a92c-59c8c23346e9";
    if (isNegotiation) return { main: "A negociar", sub: "" };

    const isAnnual = (plan.iugu_id || "").startsWith("anual");
    const period = isAnnual ? "year" : "month";

    const chosen = payableWithByPlanId[plan.id];
    const payableWith = chosen || "credit_card";

    const priceCents = toCents(plan.price);
    const discountCents = calcDiscountCents(priceCents, period, payableWith);
    const totalCents = Math.max(priceCents - discountCents, 0);

    const main =
      `${formatCurrencyValue(decimalFormat(totalCents / 100))}` +
      (isAnnual ? "/ano" : "/mês");

    let sub = "";
    if (isAnnual) {
      const monthlyEqCents = roundHalfEven(totalCents, 12);
      sub = `Equivalente a ${formatCurrencyValue(
        decimalFormat(monthlyEqCents / 100)
      )}/mês`;
    }

    return { main, sub };
  }

  const isNegotiationSelected =
    selectedPlan.id === "7945dc1a-1722-43db-a92c-59c8c23346e9";

  const canSelect = !!selectedPlan.id && !updating;

  return (
    <div className={`${active || show ? "row" : "d-none"}`} ref={ref}>
      {!hideButton && (
        <EasyButton
          type="primary"
          size=".9em"
          disabled={fetchingPlans}
          loading={fetchingPlans}
          label="Upgrade de Plano"
          icon="fas fa-star"
          onClick={() => {
            setOpenedByParent(false);
            getPlans();
          }}
        />
      )}

      <Modal
        size="xl"
        title="Selecione abaixo o plano"
        onHide={
          updating
            ? null
            : () => {
                setShowModal(false);
                setPlan({});
                setOpenedByParent(false);
                onRequestClose();
              }
        }
        show={showModal}
      >
        {openedByParent ? (
          <Text fontSize="md">Notas acabaram muito cedo? Considere fazer um upgrade de plano.</Text>
        ) : (
          <React.Fragment>
            <Text fontSize="md">{title}</Text>
            <Text fontSize="md">Selecione abaixo o seu novo plano de notas:</Text>
          </React.Fragment>
        )}

        <div className="d-flex justify-content-center mt-3">
          <div className="btn-group" role="group" aria-label="Tipo de plano">
            <button
              type="button"
              className={`btn ${planType === "mensal" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setPlanType("mensal")}
              disabled={updating}
              title="Mensal com 5% no cartão"
            >
              Mensal
            </button>
            <button
              type="button"
              className={`btn ${planType === "anual" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setPlanType("anual")}
              disabled={updating}
              title="Anual com 2 meses grátis (no cartão)"
            >
              Anual
            </button>
          </div>
        </div>

        <CardHorizontal className="py-4 ">
          {filteredPlans.map((plan) => {
            const isAnnual = (plan.iugu_id || "").startsWith("anual");
            const isNegotiation = plan.id === "7945dc1a-1722-43db-a92c-59c8c23346e9";
            const isSelected = selectedPlan.id === plan.id;

            const { main, sub } = renderPlanPrice(plan);

            return (
              <PlanWrapper
                key={plan.id}
                selected={isSelected}
                disabled={updating}
                onClick={
                  updating
                    ? null
                    : () => {
                        const isSame = plan.id === selectedPlan.id;
                        setPlan(isSame ? {} : plan);

                        if (!isSame && !isNegotiation) {
                          setPayableWithByPlanId((prev) => {
                            if (prev[plan.id]) return prev;
                            return { ...prev, [plan.id]: "credit_card" };
                          });
                        }
                      }
                }
              >
                <Icon className="fas fa-check" selected={isSelected} />

                <p className="text-center">Assinatura:</p>
                <h2>{plan.name}</h2>
                <h4 className="text-center">{isAnnual ? "anuais" : "mensais"}</h4>

                <h4 className="py-2 text-center">{main}</h4>

                {!!sub && (
                  <Text fontSize="sm" align="center" color="white" style={{ opacity: 0.92 }}>
                    {sub}
                  </Text>
                )}

                {isSelected && !isNegotiation && (
                  <PayMethodPills
                    active={payableWithByPlanId[plan.id] || "credit_card"}
                    onChange={(val) => onChangePayableWith(plan.id, val)}
                    disabled={updating}
                    isAnnual={isAnnual}
                  />
                )}
              </PlanWrapper>
            );
          })}
        </CardHorizontal>

        <div className="my-4">
          {selectedPlan.id ? (
            selectedPlan.id !== "7945dc1a-1722-43db-a92c-59c8c23346e9" && (
              <Text color="danger" align="center" fontSize="1.2em" py="3">
                Atenção! Após confirmação do Upgrade, não será mais possível realizar emissão de
                notas enquanto o novo plano estiver com a fatura pendente. Após pagamento, a
                ativação será liberada automaticamente. Deseja contratar um novo plano? Formas de
                pagamento: PIX e cartões de crédito (American Express, Mastercard, Visa, Elo)
              </Text>
            )
          ) : (
            <span />
          )}

          <div className="d-flex justify-content-center py-4">
            <Button
              aria-label={
                selectedPlan.name ? `Selecionar plano ${selectedPlan.name}` : "Selecionar plano"
              }
              data-id={selectedPlan.id}
              onClick={() => {
                if (!canSelect) return;

                if (isNegotiationSelected) {
                  window.open(
                    `https://wa.me/${supportPhoneNumber}?text=Olá!,+quero+negociar+um+plano+entre+31+e+99+notas+no+Easygestor`,
                    "_blank"
                  );
                  return;
                }

                canUpgrade(selectedPlan);
              }}
              disabled={!canSelect}
            >
              {updating ? "Processando, aguarde..." : "Selecionar"}
            </Button>
          </div>

          <div className="d-flex justify-content-center pb-3">
            <Button
              className="btn btn-link"
              onClick={() => {
                if (updating) return;
                setShowModal(false);
                setPlan({});
                onRequestClose();
                onSwitchToAvulso();
              }}
              aria-label="Prefiro comprar nota avulsa"
            >
              Prefiro comprar notas avulsas emergenciais
            </Button>
          </div>
        </div>
      </Modal>

      <Toast show={toast.show} type={toast.type} message={toast.message} />
    </div>
  );
}


// WEBPACK FOOTER //
// ./src/views/AppViews/Plans/CardUpgrade/index.jsx